export const SYSTEM_STATUS_KIND = {
  Maintenance: 'maintenance',
  Outage: 'outage',
} as const;

export type SystemStatusKind = (typeof SYSTEM_STATUS_KIND)[keyof typeof SYSTEM_STATUS_KIND];

export interface SystemStatusPayload {
  enabled: boolean;
  kind: SystemStatusKind;
  title: string;
  message: string;
  detailUrl?: string;
  retryAfterMinutes?: number;
  updatedAt?: string;
}

export const DEFAULT_SYSTEM_STATUS: SystemStatusPayload = {
  enabled: false,
  kind: SYSTEM_STATUS_KIND.Maintenance,
  title: 'Digital Passport',
  message: 'The application is available.',
  detailUrl: '',
  retryAfterMinutes: undefined,
  updatedAt: undefined,
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isSystemStatusKind(value: unknown): value is SystemStatusKind {
  return value === SYSTEM_STATUS_KIND.Maintenance || value === SYSTEM_STATUS_KIND.Outage;
}

export function parseSystemStatusPayload(raw: unknown): SystemStatusPayload {
  if (typeof raw !== 'object' || raw === null) {
    return { ...DEFAULT_SYSTEM_STATUS };
  }

  const record = raw as Record<string, unknown>;
  const enabled = record['enabled'] === true;
  const kindRaw = record['kind'];
  const kind = isSystemStatusKind(kindRaw) ? kindRaw : SYSTEM_STATUS_KIND.Maintenance;

  const title = isNonEmptyString(record['title']) ? record['title'].trim() : DEFAULT_SYSTEM_STATUS.title;
  const message = isNonEmptyString(record['message'])
    ? record['message'].trim()
    : DEFAULT_SYSTEM_STATUS.message;

  const detailUrl = isNonEmptyString(record['detailUrl']) ? record['detailUrl'].trim() : '';
  const updatedAt = isNonEmptyString(record['updatedAt']) ? record['updatedAt'].trim() : '';

  let retryAfterMinutes: number | undefined;
  if (typeof record['retryAfterMinutes'] === 'number' && Number.isFinite(record['retryAfterMinutes'])) {
    const n = Math.floor(record['retryAfterMinutes']);
    if (n > 0) {
      retryAfterMinutes = n;
    }
  }

  return {
    enabled,
    kind,
    title,
    message,
    detailUrl: detailUrl.length > 0 ? detailUrl : undefined,
    retryAfterMinutes,
    updatedAt: updatedAt.length > 0 ? updatedAt : undefined,
  };
}
